import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Globe, Menu, X, ArrowRight, Compass, Search as SearchIcon, BookOpen, MessageCircle, MapPin } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet, SheetContent, SheetTrigger, SheetClose,
} from "@/components/ui/sheet";

export default function Header() {
  const { t, lang, setLang, localizePath } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const loc = useLocation();
  const navigate = useNavigate();
  const onHero = loc.pathname === "/" || loc.pathname === "/it" || loc.pathname === "/en";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  const transparent = onHero && !scrolled;
  const go = (path) => navigate(localizePath(path));

  const mobileLinks = [
    { to: "/come-funziona", label: t("nav.howItWorks"), icon: Compass, testId: "mobile-nav-howitworks" },
    { to: "/search", label: t("nav.search"), icon: SearchIcon, testId: "mobile-nav-search" },
    { to: "/destinazioni", label: lang === "en" ? "Destinations" : "Destinazioni", icon: MapPin, testId: "mobile-nav-destinations" },
    { to: "/centro-aiuto", label: t("nav.help"), icon: BookOpen, testId: "mobile-nav-prima-di-salpare" },
    { to: "/contatti", label: t("nav.contact"), icon: MessageCircle, testId: "mobile-nav-contatti" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        transparent ? "bg-transparent border-transparent" : "bg-white/80 backdrop-blur-xl border-b border-[#E2E8F0]"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-10 h-16 flex items-center justify-between">
        <Link to={localizePath("/")} data-testid="logo-link" className="flex items-center gap-2 group">
          <img
            src="https://customer-assets.emergentagent.com/job_charter-connect-beta/artifacts/9lb67pg5_logo%20seamatch.png"
            alt="Seamatch"
            className="h-10 w-10 rounded-full object-cover"
          />
          <span className={`font-serif text-xl tracking-tight ${transparent ? "text-white" : "text-navy"}`}>Seamatch</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-7">
          <button
            onClick={() => go("/come-funziona")}
            data-testid="nav-howitworks"
            className={`text-sm font-medium hover:opacity-70 transition ${transparent ? "text-white/90" : "text-navy"}`}
          >
            {t("nav.howItWorks")}
          </button>
          <button
            onClick={() => go("/search")}
            data-testid="nav-search"
            className={`text-sm font-medium hover:opacity-70 transition ${transparent ? "text-white/90" : "text-navy"}`}
          >
            {t("nav.search")}
          </button>
          <button
            onClick={() => go("/destinazioni")}
            data-testid="nav-destinations"
            className={`text-sm font-medium hover:opacity-70 transition ${transparent ? "text-white/90" : "text-navy"}`}
          >
            {lang === "en" ? "Destinations" : "Destinazioni"}
          </button>
          <button
            onClick={() => go("/centro-aiuto")}
            data-testid="nav-centro-aiuto"
            className={`text-sm font-medium hover:opacity-70 transition ${transparent ? "text-white/90" : "text-navy"}`}
          >
            {t("nav.help")}
          </button>
          <button
            onClick={() => go("/contatti")}
            data-testid="nav-contatti"
            className={`text-sm font-medium hover:opacity-70 transition ${transparent ? "text-white/90" : "text-navy"}`}
          >
            {t("nav.contact")}
          </button>
        </nav>

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="lang-switcher"
                className={`h-9 px-3 inline-flex items-center gap-1.5 rounded-md text-xs font-semibold uppercase tracking-wider border transition ${
                  transparent ? "border-white/40 text-white hover:bg-white/10" : "border-[#E2E8F0] text-navy hover:bg-sand"
                }`}
              >
                <Globe className="h-3.5 w-3.5" />
                {lang}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" data-testid="lang-menu">
              <DropdownMenuItem data-testid="lang-it" onClick={() => setLang("it")}>Italiano</DropdownMenuItem>
              <DropdownMenuItem data-testid="lang-en" onClick={() => setLang("en")}>English</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Desktop CTA */}
          <Button
            data-testid="cta-header-search"
            onClick={() => go("/search")}
            className={`hidden sm:inline-flex h-9 rounded-md text-sm font-medium px-4 ${
              transparent ? "bg-white text-navy hover:bg-sand" : "bg-navy text-white hover:bg-[#06101f]"
            }`}
          >
            {t("nav.search")}
          </Button>

          {/* Mobile hamburger */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-trigger"
                aria-label={lang === "en" ? "Open menu" : "Apri menu"}
                className={`md:hidden h-10 w-10 inline-flex items-center justify-center rounded-md border transition ${
                  transparent ? "border-white/40 text-white hover:bg-white/10" : "border-[#E2E8F0] text-navy hover:bg-sand"
                }`}
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              data-testid="mobile-menu-panel"
              className="w-[88%] max-w-sm bg-white border-l border-[#E2E8F0] p-0 [&>button.absolute]:hidden"
            >
              <div className="h-full flex flex-col">
                {/* Header inside drawer */}
                <div className="flex items-center justify-between px-6 h-16 border-b border-[#E2E8F0]">
                  <Link to={localizePath("/")} className="flex items-center gap-2" onClick={() => setMobileOpen(false)}>
                    <img
                      src="https://customer-assets.emergentagent.com/job_charter-connect-beta/artifacts/9lb67pg5_logo%20seamatch.png"
                      alt="Seamatch"
                      className="h-8 w-8 rounded-full object-cover"
                    />
                    <span className="font-serif text-lg text-navy">Seamatch</span>
                  </Link>
                  <SheetClose asChild>
                    <button
                      data-testid="mobile-menu-close"
                      aria-label={lang === "en" ? "Close menu" : "Chiudi menu"}
                      className="h-9 w-9 inline-flex items-center justify-center rounded-md text-navy hover:bg-sand transition"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </SheetClose>
                </div>

                {/* Nav list */}
                <nav className="flex-1 overflow-y-auto py-6">
                  {mobileLinks.map((l) => {
                    const Icon = l.icon;
                    const localPath = localizePath(l.to);
                    const active = loc.pathname === localPath || (l.to !== "/" && loc.pathname.startsWith(localPath));
                    return (
                      <button
                        key={l.to}
                        data-testid={l.testId}
                        onClick={() => { navigate(localPath); setMobileOpen(false); }}
                        className={`w-full flex items-center justify-between px-6 py-4 border-b border-[#F1F5F9] transition group ${
                          active ? "bg-sand" : "hover:bg-sand/60"
                        }`}
                      >
                        <span className="flex items-center gap-3">
                          <span className={`h-9 w-9 rounded-full flex items-center justify-center transition ${
                            active ? "bg-navy text-white" : "bg-sand text-navy group-hover:bg-navy group-hover:text-white"
                          }`}>
                            <Icon className="h-4 w-4" />
                          </span>
                          <span className="font-serif text-lg text-navy">{l.label}</span>
                        </span>
                        <ArrowRight className="h-4 w-4 text-ink/40 group-hover:text-navy group-hover:translate-x-1 transition" />
                      </button>
                    );
                  })}

                  {/* Language switcher inline */}
                  <div className="px-6 pt-6">
                    <p className="text-[11px] uppercase tracking-wider text-ink/50 mb-2">{t("nav.language")}</p>
                    <div className="flex gap-2">
                      <button
                        data-testid="mobile-lang-it"
                        onClick={() => setLang("it")}
                        className={`flex-1 h-10 rounded-md border text-sm font-semibold transition ${
                          lang === "it" ? "bg-navy text-white border-navy" : "border-[#E2E8F0] text-navy hover:bg-sand"
                        }`}
                      >
                        Italiano
                      </button>
                      <button
                        data-testid="mobile-lang-en"
                        onClick={() => setLang("en")}
                        className={`flex-1 h-10 rounded-md border text-sm font-semibold transition ${
                          lang === "en" ? "bg-navy text-white border-navy" : "border-[#E2E8F0] text-navy hover:bg-sand"
                        }`}
                      >
                        English
                      </button>
                    </div>
                  </div>
                </nav>

                {/* Footer CTA */}
                <div className="px-6 py-5 border-t border-[#E2E8F0] bg-sand/40">
                  <Button
                    data-testid="mobile-cta-search"
                    onClick={() => { go("/search"); setMobileOpen(false); }}
                    className="w-full h-12 rounded-full bg-navy text-white hover:bg-[#06101f] text-sm font-semibold"
                  >
                    {t("nav.search")}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                  <p className="mt-3 text-center text-xs text-ink/60">
                    <a href="mailto:info@seamatch.pro" className="hover:text-navy">info@seamatch.pro</a>
                  </p>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
